import { ClickAwayListener } from '@mui/material';
import { BlueIconButton, ButtonWrapper, StyledMenu, StyledMenuItem } from './ContextMenuButton.styles';
import { useCallback, useEffect, useRef, useState } from 'react';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import type { CellContext } from '@tanstack/react-table';
import { useContextMenu } from '../../../../contexts/ContextMenuProvider.tsx';
import { Button } from '../../../mui/common/Button.tsx';

interface HeliosRecordDataTableColumnSettings {
  label?: string;
}

interface MenuItemConfig {
  label: string;
  action: (rowId: string) => void;
  icon?: React.ReactNode;
}

interface Props<T, P> {
  value?: string;
  row?: CellContext<T, P>;
  onClick?: (row?: T, cell?: keyof P, action?: (rowId: string) => void) => unknown;
  isClickable?: boolean;
  dataType?: string;
  settings?: HeliosRecordDataTableColumnSettings;
  menuItems?: MenuItemConfig[];
  isEditMode?: boolean;
}

const ContextMenuButtonBase = <T, P>({ row, onClick, isClickable = true, menuItems = [], isEditMode }: Props<T, P>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { activeRowId, openMenuForRow, closeMenu } = useContextMenu();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const isActive = activeRowId === row?.row?.id;

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (!isClickable) return;
    setAnchorEl(event.currentTarget);
    openMenuForRow(row?.row?.id ?? '');
  };

  const handleClose = () => {
    setAnchorEl(null);
    closeMenu();
  };

  const handleActionClick = useCallback(
    (action: (rowId: string) => void) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      handleClose();

      if (onClick) {
        onClick(row?.row?.original, row?.column?.id as keyof P, (rowId) => action(rowId));
      }
    },
    [onClick, row?.row?.original, row?.column?.id]
  );

  useEffect(() => {
    if (!anchorEl && isActive) {
      setAnchorEl(buttonRef.current);
    }
  }, [buttonRef.current, isActive]);

  return (
    <ClickAwayListener
      onClickAway={(event) => {
        event.stopPropagation();
        handleClose();
      }}>
      <>
        {isEditMode ? (
          <Button variant="outlined" ref={buttonRef} onClick={handleMenuClick} disabled={!isClickable}>
            <MoreHorizIcon style={{ opacity: isClickable ? 1 : 0.5 }} />
          </Button>
        ) : (
          <ButtonWrapper>
            <BlueIconButton ref={buttonRef} onClick={handleMenuClick} disabled={!isClickable}>
              <MoreHorizIcon />
            </BlueIconButton>
          </ButtonWrapper>
        )}

        <StyledMenu anchorEl={anchorEl} open={open} onClose={handleClose}>
          {menuItems.map((item, index) => (
            <StyledMenuItem key={`item-${item.label}-${index}`} onClick={handleActionClick(item.action)}>
              {item.icon && <span style={{ marginRight: 8 }}>{item.icon}</span>}
              {item.label}
            </StyledMenuItem>
          ))}
        </StyledMenu>
      </>
    </ClickAwayListener>
  );
};

export const ContextMenuButton = ContextMenuButtonBase as typeof ContextMenuButtonBase;
